import { noop } from 'lodash';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { AIOnboardingDocumentPanelProps } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { SuggestionForSearchAndEvidence } from '@/modules/aiSuggestions/aiSuggestions.contracts';

/**
 * SuggestionEvidence holds the information about the selected suggestion to
 * show the evidence for. We use this object to communicate the annotations and
 * search bar text between the entity forms and the document viewer.
 */

export interface SelectedSuggestionEvidenceContext {
  /**
   * Locate the evidence for a suggestion in the doc viewer.
   * @param suggestion
   */
  locateSuggestion: (suggestion: SuggestionForSearchAndEvidence) => void;

  /**
   * The evidence for the selected suggestion to show in the doc viewer.
   */
  documentPanelProps: AIOnboardingDocumentPanelProps;
  setDocumentPanelProps: Dispatch<
    SetStateAction<AIOnboardingDocumentPanelProps>
  >;
}

export const getDefaultValues = (): SelectedSuggestionEvidenceContext => ({
  locateSuggestion: noop,
  documentPanelProps: {
    activeDocumentViewerTab: undefined,
    activeDocumentId: undefined,
    searchQuery: '',
    searchLoading: false,
    annotations: undefined,
    activeAnnotationIndex: undefined,
  },
  setDocumentPanelProps: noop,
});

export const SelectedSuggestionEvidenceContext =
  createContext<SelectedSuggestionEvidenceContext>(getDefaultValues());

export const useSelectedSuggestionEvidenceContext = () => {
  return useGuardedContext(
    SelectedSuggestionEvidenceContext,
    'SelectedSuggestionEvidenceProvider'
  );
};

/**
 * Optional version of useSelectedSuggestionEvidenceContext that returns the default
 * values when used outside of a SelectedSuggestionEvidenceProvider.
 * Use this when you want to optionally use the context if available.
 */
export const useOptionalSelectedSuggestionEvidenceContext = () => {
  return useContext(SelectedSuggestionEvidenceContext);
};
